/**
 * This is an abstraction of the Request class. It has several key benefits (compared to instantiating Request):
 * - easier to create a request
 * - easier to tweak parameters and options
 * - errors are handled in a standard way - parsing and dealing with potential server errors (e.g. "lasterror")
 * - supports IMessageData which plays well with different message components
 * - tracks cache and avoids populating messageData if response is immediately available
 */

import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IResponse} from "../index";
import {SPINNER} from "../constants";
import {global, lm} from "../appContext";
import {TServiceName} from "../constants/ApiServices";
import {nsRequest} from "./fn";
import {ERROR_MESSAGE} from "../constants/Language";

export interface IRequestServiceOptions {
    serviceName?: string
    reqParams?: any
    successCallback?: Function
    errorCallback?: Function
    messageData?: KnockoutObservable<IMessageData | null>
    requestMessage?: string
    successMessage?: string
    errorMessage?: string
    emptyMessage?: string
    shouldParseResponse?: boolean
    isRequesting?: KnockoutObservable<boolean>
    isSuccess?: KnockoutObservable<boolean>
    isError?: KnockoutObservable<boolean>
}

export default class RequestService {
    static REQUEST_MESSAGE = `Laster data... ${SPINNER}`;
    static ERROR_MESSAGE = `Oooops! Noe gikk galt.`;

    private static dummyFunction() {
    }

    messageData!: KnockoutObservable<IMessageData | null>;

    isRequesting!: KnockoutObservable<boolean>;
    isSuccess!: KnockoutObservable<boolean>;
    isError!: KnockoutObservable<boolean>;

    requestMessage!: string;
    successMessage!: string;
    errorMessage!: string;
    emptyMessage!: string;

    resp: any | IResponse;

    private shouldParseResponse: boolean = true;
    private errorCallback!: Function;
    private successCallback!: Function;

    protected serviceName!: TServiceName;
    private reqParams: any;
    private jQueryXHR: JQueryXHR | null = null;

    constructor(options: IRequestServiceOptions) {
        this.setOptions(options);
    }

    setOptions(options: IRequestServiceOptions) {
        _.extend(this, {
            reqParams: this.reqParams || {},
            successCallback: this.successCallback || RequestService.dummyFunction,
            errorCallback: this.errorCallback || RequestService.dummyFunction,
            messageData: this.messageData || ko.observable(null),
            errorMessage: this.errorMessage || ERROR_MESSAGE,
            shouldParseResponse: this.shouldParseResponse,
            isRequesting: this.isRequesting || ko.observable(false),
            isSuccess: this.isSuccess || ko.observable(false),
            isError: this.isError || ko.observable(false)
        }, options);
    }

    request(params: any = null) {
        this.abortXHR();
        const route = global.router.currentRoute();
        const p = nsRequest.getParams(this.serviceName, params, this.reqParams, route?.__routeParams ?? {})
        if (nsRequest.abortIfMissingMandatoryToken(this.serviceName, p)) {
            this.isSuccess(false);
            this.isError(true);
            this.isRequesting(false);
            this.errorCallback(lm.t('not authenticated'));
            return;
        }
        this.isSuccess(false);
        this.isError(false);
        this.isRequesting(true);
        this.resp = null;
        this.jQueryXHR = global.req.r(this.serviceName, p, {
            success: this.onSuccess.bind(this),
            error: this.onError.bind(this)
        });
        if (!this.isCached(p)) {
            this.produceMessage(this.requestMessage, "info");
        }
    }

    isCached(params: any = {}): boolean {
        return global.cacheManager.isCached(this.serviceName, params, false);
    }

    abort() {
        this.abortXHR();
        this.isRequesting(false);
        this.isSuccess(false);
        this.isError(false);
        this.messageData(null);
    }

    protected abortXHR() {
        if (this.jQueryXHR) {
            this.jQueryXHR.abort();
            this.jQueryXHR = null;
        }
    }

    private onSuccess(resp: IResponse | null) {
        this.isRequesting(false);
        this.jQueryXHR = null;

        if (!resp) {
            this.produceMessage(this.errorMessage, "danger");
            this.isError(true);
            this.errorCallback();
            return;
        }

        const returnCode: number = parseInt(resp.returncode); // 0 equals no error
        const lastError = resp.lasterror;

        if (returnCode || lastError) {
            switch (returnCode) {
                case 2:
                    this.produceMessage(`Du har ikke tilgang til denne informasjonen. Ta kontakt med IT-support for utvidet tilgang.`, "danger", lastError);
                    break;
                default:
                    this.produceMessage(this.errorMessage, "danger", lastError);
                    break;
            }
            this.isError(true);
            this.errorCallback(resp, returnCode, lastError);
            return;
        }

        // okay so the response seems error-free
        this.isError(false);
        this.isSuccess(true);

        if (this.emptyMessage && (!resp.data || !resp.data.length)) {
            this.produceMessage(this.emptyMessage, "info");
        } else if (this.successMessage) {
            this.produceMessage(this.successMessage, "success");
        } else {
            // let's wait for next render to check if we should remove the existing message
            // (if it then has changed we'll not touch the message data)
            let oldMessageData = this.messageData();
            setTimeout(() => {
                if (this.messageData() == oldMessageData) {
                    this.messageData(null);
                }
            }, 1);
        }

        this.resp = resp;

        this.successCallback(
            this.shouldParseResponse
                ? resp.data
                : resp
        );
    }

    private onError(jqXHR?: JQueryXHR, textStatus?: string, errorThrown?: string) {
        this.isRequesting(false);
        this.isError(true);
        this.isSuccess(false);
        this.jQueryXHR = null;
        this.produceMessage(this.errorMessage, "danger", jqXHR?.responseText || errorThrown);
        this.errorCallback(jqXHR, textStatus, errorThrown);
    }

    protected produceMessage(message?: string, type: "info" | "danger" | "warning" | "success" = "info", reason?: string) {
        if (!message) {
            this.messageData(null);
            return;
        }
        if (reason) {
            message += `<p>Årsak: ${reason}</p>`;
        }

        this.messageData({
            message: message,
            type: type
        });
    }

    setParams(params: any) {
        this.reqParams = params;
    }

}
